import React, { useEffect, useState, useCallback } from "react";
import { UploadCloud, CheckCircle2, Trash2, Loader2, Film, Image as ImageIcon, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const SLOTS = ["The Outage", "Kojo Discovers", "Kojo Pitches", "Testimonial", "Silent vs Diesel"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminFilms = () => {
  const [films, setFilms] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [posterUploading, setPosterUploading] = useState(null);
  const [heroIndex, setHeroIndex] = useState(null);

  const load = useCallback(() => {
    api.get("/films").then((r) => setFilms(r.data)).catch(() => {});
    api.get("/hero-film").then((r) => setHeroIndex(r.data.index)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setHero = async (index) => {
    const next = heroIndex === index ? null : index;
    try {
      await api.post("/admin/hero-film", { index: next });
      setHeroIndex(next);
      toast.success(next === null ? "Hero reel cleared" : `"${SLOTS[index]}" set as hero reel`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set hero reel");
    }
  };

  const upload = async (index, file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    setUploading(index);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/films/${index}`, fd);
      toast.success(`Film uploaded to "${SLOTS[index]}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const uploadPoster = async (index, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPosterUploading(index);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/films/${index}/poster`, fd);
      toast.success(`Thumbnail set for "${SLOTS[index]}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Thumbnail upload failed");
    } finally {
      setPosterUploading(null);
    }
  };

  const removePoster = async (index) => {
    try {
      await api.delete(`/admin/films/${index}/poster`);
      toast.success("Thumbnail removed — default restored");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const remove = async (index) => {
    try {
      await api.delete(`/admin/films/${index}`);
      toast.success("Film removed — placeholder restored");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const byIndex = Object.fromEntries(films.map((f) => [f.index, f]));

  return (
    <div data-testid="admin-films">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
        Story Films · upload your campaign videos & the Silent-vs-Diesel clip (mp4, up to 150MB)
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {SLOTS.map((label, i) => {
          const f = byIndex[i];
          const busy = uploading === i;
          const pbusy = posterUploading === i;
          return (
            <div key={i} className="rounded-xl border border-white/10 bg-[#181818] p-5" data-testid={`film-slot-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-lime2">0{i + 1}</span>
                  <h4 className="font-display font-bold">{label}</h4>
                </div>
                <div className="flex items-center gap-3">
                  {f?.has_upload && (
                    <button
                      onClick={() => setHero(i)}
                      data-testid={`film-hero-${i}`}
                      title={heroIndex === i ? "Hero reel — click to clear" : "Set as hero reel"}
                      className={`inline-flex items-center gap-1 text-xs transition-colors ${heroIndex === i ? "text-lime2" : "text-white/30 hover:text-lime2"}`}
                    >
                      <Star size={14} fill={heroIndex === i ? "currentColor" : "none"} />
                      {heroIndex === i ? "Hero" : ""}
                    </button>
                  )}
                  {f?.has_upload ? (
                    <span className="inline-flex items-center gap-1 text-xs text-lime2"><CheckCircle2 size={14} /> Live</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-white/40"><Film size={14} /> Placeholder</span>
                  )}
                </div>
              </div>

              <div className="mt-4 flex gap-4">
                {/* Thumbnail preview */}
                <div className="relative w-28 shrink-0 aspect-video rounded-lg overflow-hidden border border-white/10 bg-black" data-testid={`film-thumb-${i}`}>
                  {f?.has_poster ? (
                    <img src={`${BACKEND_URL}${f.poster_url}`} alt={`${label} thumbnail`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white/25"><ImageIcon size={20} /></div>
                  )}
                  <span className="absolute bottom-0 inset-x-0 text-[9px] text-center bg-black/60 text-white/60 py-0.5">
                    {f?.has_poster ? (f.poster_auto ? "Auto" : "Custom") : "Default"}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-white/50 text-xs truncate">
                    {f?.has_upload ? f.original_filename : "Using stock placeholder footage"}
                  </p>
                  <div className="mt-3 flex flex-col gap-2">
                    <label className={`inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 hover:border-lime2 hover:text-lime2 h-9 text-xs cursor-pointer transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`}>
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                      {busy ? "Uploading..." : f?.has_upload ? "Replace film" : "Upload film"}
                      <input type="file" accept="video/*" className="hidden" data-testid={`film-input-${i}`} onChange={(e) => upload(i, e.target.files?.[0])} />
                    </label>
                    <label className={`inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 hover:border-lime2 hover:text-lime2 h-9 text-xs cursor-pointer transition-colors ${pbusy ? "opacity-60 pointer-events-none" : ""}`}>
                      {pbusy ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                      {pbusy ? "Uploading..." : f?.has_poster ? "Replace thumbnail" : "Set thumbnail"}
                      <input type="file" accept="image/*" className="hidden" data-testid={`poster-input-${i}`} onChange={(e) => uploadPoster(i, e.target.files?.[0])} />
                    </label>
                  </div>
                  <div className="mt-2 flex gap-3">
                    {f?.has_poster && !f.poster_auto && (
                      <button onClick={() => removePoster(i)} data-testid={`poster-delete-${i}`} className="inline-flex items-center gap-1 text-[11px] text-white/40 hover:text-red-400 transition-colors">
                        <Trash2 size={12} /> thumbnail
                      </button>
                    )}
                    {f?.has_upload && (
                      <button onClick={() => remove(i)} data-testid={`film-delete-${i}`} className="inline-flex items-center gap-1 text-[11px] text-white/40 hover:text-red-400 transition-colors">
                        <Trash2 size={12} /> film
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
